import { useCallback, useEffect } from 'react'

import { useSearchParams } from 'react-router-dom'

import { useLocalStorage } from './useLocalStorage'

import type { Nullable } from '../types/common'

const parseParam = (param: Nullable<string>): Nullable<boolean> => {
  if (param === null) {
    return null
  }

  if (['false', '0'].includes(param)) {
    return false
  }

  if (['', 'true', '1'].includes(param)) {
    return true
  }

  return null
}

/**
 * @desc allows to locally persist a boolean flag controlled from the query string
 */
export const useQueryStringFlag = (
  flag: string,
  defaultValue?: boolean,
): [enabled: Nullable<boolean>, setEnabled: (value: boolean) => void] => {
  const [enabled, setEnabled] = useLocalStorage(flag, defaultValue)
  const [searchParams, setSearchParams] = useSearchParams()

  const param = searchParams.get(flag)

  useEffect(() => {
    const parsedParam = parseParam(param)

    if (parsedParam === null) {
      return
    }

    if (enabled === null) {
      setEnabled(parsedParam)
    }

    if (enabled && !parsedParam) {
      setEnabled(false)
    }

    if (!enabled && parsedParam) {
      setEnabled(true)
    }
  }, [setEnabled, enabled, flag, param])

  return [
    enabled,
    useCallback(
      (value: boolean) =>
        setSearchParams((params) => {
          params.set(flag, `${value}`)
          return params
        }),
      [setSearchParams, flag],
    ),
  ]
}
